import { useContext, useState } from 'react';
import { addParams, fetchWithTokenRefresh } from '../../../../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../../../../api/endpoints';
import { useDialog } from '../../../../../../../common/dialog/useDialog';
import { UserInvitationData } from '../../interfaces';
import { ToastNotificationContext } from '../../../../../../../common/toast-notification';
import { handleResponseError } from '../../../../../../../../api/handleResponseError';
import { DialogError } from '../../../../../../../common/business-error';
import { ToastProps } from '../../../../../../../common/toast-notification/interfaces';
import { userInvitationBusinessError } from '../businessErrors';

type ApiResponse = {
  message: string;
  status: ApiResponseStatus;
};

enum ApiResponseStatus {
  OK = 'OK',
  UNKNOWN_ROLE = 'UNKNOWN_ROLE',
  LIMIT_EXCEEDED = 'LIMIT_EXCEEDED',
  EMAIL_ALREADY_USED = 'EMAIL_ALREADY_USED'
}

interface Props {
  handleSuccess: () => void;
}

export const useSendUserInvitation = ({ handleSuccess }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [inviteLoading, setLoading] = useState(false);
  const [inviteError, setError] = useState<DialogError>(null);

  const handleUserInvite = async (userData: UserInvitationData) => {
    try {
      setLoading(true);
      const response = await fetchWithTokenRefresh(endpoints.accountsManagementInactive, addParams('POST', userData));
      const jsonResponse = await response.json();

      if (response.status !== 200) {
        await handleResponseError(response);
      }

      const parsed: ApiResponse = jsonResponse;
      switch (parsed.status) {
        case ApiResponseStatus.OK: {
          openToast({
            message: 'Invitation was send!',
            severity: 'success'
          });
          setLoading(false);
          handleCloseDialog();
          handleSuccess();
          break;
        }
        case ApiResponseStatus.UNKNOWN_ROLE: {
          const toastResponse: ToastProps = {
            message: 'Unknown role provided',
            severity: 'error'
          };
          openToast(toastResponse);
          setError({ message: toastResponse.message, severity: 'error' });
          break;
        }
        case ApiResponseStatus.EMAIL_ALREADY_USED: {
          const toastResponse: ToastProps = {
            message: 'Email already in use',
            severity: 'error'
          };
          openToast(toastResponse);
          setError({ message: toastResponse.message, severity: 'error' });
          break;
        }
        case ApiResponseStatus.LIMIT_EXCEEDED: {
          const toastResponse: ToastProps = {
            message: 'Limit of users invitation exceeded',
            severity: 'error'
          };
          openToast(toastResponse);
          setError({ message: toastResponse.message, severity: 'error' });
          break;
        }
        default: {
          const toastResponse: ToastProps = {
            message: 'unknown error',
            severity: 'error'
          };
          openToast(toastResponse);
          setError({ message: toastResponse.message, severity: 'error' });
          break;
        }
      }
      setLoading(false);
    } catch (e) {
      const { message, severity } = userInvitationBusinessError();

      setError({ message, severity });
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    userInvitationModalOpen: isDialogOpen,
    handleOpenUserInvitationModal: handleOpenDialog,
    handleCloseUserInvitationModal: closeDialog,
    handleUserInvitation: handleUserInvite,
    inviteError,
    inviteLoading
  };
};
